import {
  useProjectId,
  usePackageId,
  useServiceName,
  usePackageByTypeAndPackageId,
} from '@consigli/hooks';
import { DocumentPackageType, ServiceName } from '@consigli/types';
import { type FC, useMemo } from 'react';
import { Outlet } from 'react-router';

import { Breadcrumb } from '@/features/breadcrumb/breadcrumb';
import { DocumentListProvider } from '@/features/document-list/document-list-context';
import { AgentChatHost } from '@/pages/insight/propchat/agent-chat-host';
import { AgentChatAttachmentsProvider } from '@/pages/insight/propchat/use-agent-chat-attachments';
import { AgentChatSessionProvider } from '@/pages/insight/propchat/use-agent-chat-session';
import { Route, ServiceIdIndexValue } from '@/routes';

export const InsightDetailWrapper: FC = () => {
  const projectId = useProjectId();
  const packageId = usePackageId();
  const serviceName = useServiceName();

  const { pkg } = usePackageByTypeAndPackageId(
    projectId,
    ServiceIdIndexValue[serviceName] as DocumentPackageType,
    packageId,
  );

  const defaultRoute = useMemo(() => {
    if (serviceName === ServiceName.SITE_DUE_DILIGENCE) return Route.REPORTS;
    if (serviceName === ServiceName.TENDER_DOCUMENTS) return Route.DASHBOARD;
    return Route.DASHBOARD;
  }, [serviceName]);

  return (
    <>
      <Breadcrumb
        label={pkg?.name ?? ''}
        to={`/${Route.PROJECTS}/${projectId}/${Route.SERVICES}/${serviceName}/${Route.PACKAGES}/${packageId}/${defaultRoute}`}
      />
      <DocumentListProvider>
        {/* Above the Outlet, unlike the session below it: what is attached to
            the next question is written from the pages inside the Outlet - the
            Findings cards' Add to Chat - and read by the drawer, which unmounts
            whenever it is closed. */}
        <AgentChatAttachmentsProvider packageId={packageId}>
          <Outlet />
          {/* Inside DocumentListProvider because opening a cited document renders
              the shared PDF viewer, which reads that context — but outside the
              Outlet, so a streaming answer re-renders only the drawer rather than
              the whole workspace page on every character. */}
          <AgentChatSessionProvider packageId={packageId} projectId={projectId}>
            <AgentChatHost />
          </AgentChatSessionProvider>
        </AgentChatAttachmentsProvider>
      </DocumentListProvider>
    </>
  );
};
